// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Groups as data (ruxd-wmc.2): a group is a flat set of colleagues — no
 * leader, no roles inside it. Who may run groups is the account's global role
 * (`canManageGroups`); this module holds what a group name may be, the order
 * lists are shown in, who may see a group's cases and the Danish line under
 * each refusal. Kept free of React so it is unit-tested in Node;
 * `routes/GroupsPage.tsx` only renders it.
 */

import { ApiRequestError } from '../api/client';
import type { GlobalRole, Group, GroupMember } from '../api/types';
import { canManageGroups } from '../app/auth';

/** ruxd's `kMaxGroupName`: a trimmed name is at most this many UTF-8 bytes. */
export const GROUP_NAME_MAX = 100;

export type Check<T> = { ok: true; value: T } | { ok: false; reason: string };

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

/**
 * A group name as ruxd will take it: trimmed, not empty, at most
 * `GROUP_NAME_MAX` bytes, no control characters. `current` (a rename) refuses
 * an unchanged name. Uniqueness (case-insensitive) is the server's to say (409).
 */
export function groupNameCheck(raw: string, current?: string): Check<string> {
  const name = raw.trim();
  if (name === '') return { ok: false, reason: 'Giv gruppen et navn.' };
  if (new TextEncoder().encode(name).length > GROUP_NAME_MAX)
    return { ok: false, reason: `Navnet er for langt (højst ${GROUP_NAME_MAX} tegn).` };
  if (CONTROL.test(name)) return { ok: false, reason: 'Navnet må ikke indeholde kontroltegn.' };
  if (current !== undefined && name === current.trim()) return { ok: false, reason: 'Navnet er uændret.' };
  return { ok: true, value: name };
}

/** Groups: the caller's own first, then by name (Danish collation). */
export function sortGroups(groups: readonly Group[]): Group[] {
  return [...groups].sort(
    (a, b) =>
      Number(b.is_member) - Number(a.is_member) || a.name.localeCompare(b.name, 'da') || a.id.localeCompare(b.id),
  );
}

/** A member's name as lists show it: the username, else the e-mail. */
export function memberName(member: GroupMember): string {
  return member.user.display_name || member.user.email;
}

/** Members by name (no roles to rank by). */
export function sortGroupMembers(members: readonly GroupMember[]): GroupMember[] {
  return [...members].sort(
    (a, b) => memberName(a).localeCompare(memberName(b), 'da') || a.user.id.localeCompare(b.user.id),
  );
}

/**
 * Whether the caller may list the cases shared with `group`: its members,
 * group managers and admins. Anyone else gets a 403, so the page does not ask.
 */
export function canSeeGroupCases(group: Pick<Group, 'is_member'>, globalRole: GlobalRole | undefined): boolean {
  return group.is_member || canManageGroups(globalRole);
}

/** "1 medlem", "4 medlemmer". */
export function memberCountText(count: number): string {
  return `${count} ${count === 1 ? 'medlem' : 'medlemmer'}`;
}

/** "Ingen sager", "1 sag", "3 sager". */
export function caseCountText(count: number): string {
  if (count === 0) return 'Ingen sager';
  return `${count} ${count === 1 ? 'sag' : 'sager'}`;
}

/**
 * The group to show: `wanted` while it is in the list, else the first group
 * as the list is sorted (one of the caller's own, when they are in any), else none.
 */
export function selectGroup(groups: readonly Group[], wanted: string | null | undefined): string | null {
  if (wanted && groups.some((g) => g.id === wanted)) return wanted;
  return sortGroups(groups)[0]?.id ?? null;
}

/** What a group request was, for its error line. */
export type GroupAction =
  | 'list'
  | 'load'
  | 'cases'
  | 'create'
  | 'rename'
  | 'delete'
  | 'add'
  | 'remove'
  | 'leave';

/** The Danish line under a refused group request. */
export function groupErrorText(cause: unknown, action: GroupAction): string {
  if (cause instanceof ApiRequestError) {
    const { status, message } = cause;
    if (status >= 500) return 'Serveren svarer ikke lige nu. Prøv igen om lidt.';
    if (status === 400) {
      if (action === 'create' || action === 'rename') return 'Navnet er ugyldigt. Brug 1–100 tegn uden kontroltegn.';
      if (action === 'add') return 'Skriv en gyldig e-mail.';
      return 'Serveren forstod ikke ændringen.';
    }
    if (status === 403) {
      if (action === 'cases') return 'Kun gruppens medlemmer kan se, hvilke sager der er delt med den.';
      if (action === 'leave') return 'Du kan ikke forlade gruppen.';
      return 'Kun gruppeadministratorer og administratorer kan ændre grupper.';
    }
    if (status === 404) {
      if (action === 'add' && !/group/i.test(message))
        return 'Der er ingen bruger med den e-mail. En administrator opretter brugere.';
      if (action === 'remove') return 'Personen er ikke længere med i gruppen.';
      if (action === 'leave') return 'Du er ikke længere med i gruppen.';
      return 'Gruppen findes ikke længere.';
    }
    if (status === 409) {
      if (action === 'create' || action === 'rename') return 'Der findes allerede en gruppe med det navn.';
      if (action === 'add') return 'Personen er allerede med i gruppen.';
      return 'Serveren kører i lokal tilstand og har ingen grupper.';
    }
  }
  if (action === 'list') return 'Grupperne kunne ikke hentes.';
  if (action === 'load') return 'Gruppen kunne ikke hentes.';
  if (action === 'cases') return 'Gruppens sager kunne ikke hentes.';
  return 'Ændringen kunne ikke gemmes.';
}
